import { useReducer } from 'react';
import { useActionDispatchContextState } from 'lib/ActionDispatchContext';

import {
  ActionType,
  requestActionCreator,
  successActionCreator,
  failureActionCreator,
} from '../actionCreators';
import { successToast } from 'lib/components/toasts/success';
import { useToastError } from './useToastError';
import { createTranscription } from 'lib/api/transcriptApi';
import { VideoTranscriptionBody } from 'lib/api';

interface VideoTranscriptionResponse {
  success: boolean;
  data: {
    VideoID: string;
    VideUserIDoID: string;
    OriginalTranscript: string;
    ModifiedTranscript: string;
    Enabled: number;
    Status: string;
  };
}

export interface VideoTranscriptionMutateData {
  videoId: string;
  mediaBody: VideoTranscriptionBody;
}

type MutationResult = {
  data: VideoTranscriptionResponse;
  loading: boolean;
  error: string | null;
};

export type VideoTranscriptionUploadMutate = (
  body: VideoTranscriptionMutateData
) => Promise<MutationResult>;

interface VideoTranscription {
  data: VideoTranscriptionResponse;
  loading: boolean;
  error: string | null;
  mutate: VideoTranscriptionUploadMutate;
}

interface VideoTranscriptionStoreState {
  data: VideoTranscriptionResponse | undefined;
  error: string | null;
  loading: boolean;
  isValid: boolean;
}

function reducer(state: VideoTranscriptionStoreState, action: ActionType) {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        data: undefined,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
}

export const useVideoTranscriptionUpdate = (): VideoTranscription => {
  const { showError } = useToastError();
  const { setAction } = useActionDispatchContextState();

  const initialState = {
    data: undefined,
    loading: false,
    error: null,
    isValid: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const mutate: VideoTranscriptionUploadMutate = async ({
    videoId,
    mediaBody,
  }) => {
    dispatch(
      requestActionCreator({
        type: 'FETCH_REQUEST',
      })
    );

    const updateResult = await createTranscription(videoId, mediaBody);

    if (updateResult instanceof Error) {
      const parsedError = updateResult.message;

      dispatch(
        failureActionCreator({
          type: 'FETCH_FAILURE',
          payload: parsedError,
        })
      );

      showError(updateResult);

      setAction({ type: 'UPDATE_VIDEO_TRANSCRIPTION' });

      return Promise.reject({
        loading: false,
        error: parsedError,
      });
    } else {
      dispatch(
        successActionCreator({
          type: 'FETCH_SUCCESS',
          payload: mediaBody,
        })
      );

      successToast({ title: 'Subtitles updated!' });

      return {
        data: updateResult.data,
        loading: false,
        error: null,
      };
    }
  };

  const { data, error, loading } = state;

  return {
    mutate,
    data,
    loading,
    error,
  };
};
